import { Component, inject, OnInit } from '@angular/core';
import { ToastModule } from 'primeng/toast';
import { Customer, CustomerPayload, CustomerWithBalanceDate } from '../../models/customer.model';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { catchError, delay, EMPTY, Observable, take, tap } from 'rxjs';
import { CustomerService } from '../../services/customer.service';
import { MessageService } from 'primeng/api';
import { AutoFocusModule } from 'primeng/autofocus';
import { ButtonModule } from 'primeng/button';
import { CalendarModule } from 'primeng/calendar';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { PaginatorModule } from 'primeng/paginator';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { DatePicker } from 'primeng/datepicker';
import { NgIf } from '@angular/common';

@Component({
    selector: 'app-edit-customer',
    imports: [
        ToastModule,
        AutoFocusModule,
        ButtonModule,
        CalendarModule,
        CheckboxModule,
        DividerModule,
        InputNumberModule,
        InputTextModule,
        PaginatorModule,
        ReactiveFormsModule,
        RouterLink,
        DatePicker,
        NgIf
    ],
    templateUrl: './edit-customer.component.html',
    styleUrl: './edit-customer.component.scss',
})
export class EditCustomerComponent implements OnInit {
    fb: FormBuilder = inject(FormBuilder);
    activeRoute: ActivatedRoute = inject(ActivatedRoute);
    customerService: CustomerService = inject(CustomerService);
    messageService = inject(MessageService);
    router = inject(Router);
    customerDetails!: Customer;
    pageName = 'العميل';
    routeName = 'customers';
    customForm: FormGroup;
    validDate: string;
    maxDate: Date = new Date();
    btnSaved = false;

    ngOnInit(): void {
        this.createForm();
        this.getCustomerDetails();
    }

    createForm(): void {
        this.customForm = this.fb.nonNullable.group({
            code: [null],
            name: ['', Validators.required],
            email: [null, Validators.email],
            openingBalance: [0],
            dueBalance: [null],
            openingBalanceDate: [new Date()],
            phoneNumber: [null, [Validators.minLength(10), Validators.maxLength(25)]],
            vatNumber: [null, [Validators.minLength(7), Validators.maxLength(30)]],
            commercialRegister: [null, [Validators.maxLength(50)]],
            address: [null],
            isB2B: [false],
            buildingNumber: [null, [Validators.maxLength(5)]],
            additionalNumber: [null, [Validators.maxLength(7)]],
            streetName: [null, [Validators.maxLength(50)]],
            city: [null, [Validators.maxLength(30)]],
            district: [null, [Validators.maxLength(30)]],
            zip: [null, [Validators.min(11111), Validators.max(99999)]],
            countryKey: ['SA', [Validators.minLength(2), Validators.maxLength(2)]],
        });
        this.customForm.controls["isB2B"].disable()
    }

    handleFormData(): void {
        const customerData: CustomerWithBalanceDate = {
            ...this.customerDetails,
            dueBalance: this.customerDetails.dueBalance !== 0 ? -this.customerDetails.dueBalance : 0,
            openingBalanceDate: this.customerDetails?.openingBalanceDate
                ? new Date(this.customerDetails.openingBalanceDate)
                : null,
        };
        this.customForm.patchValue(customerData);
        this.customForm.markAsPristine(); // Mark form as pristine after loading data
        this.dueBalanceControl()?.disable();
    }

    dueBalanceControl(): FormControl {
        return <FormControl>this.customForm.get('dueBalance');
    }

    getCustomerDetails(): void {
        (this.activeRoute.data as Observable<{ customerDetails: Customer }>)
            .pipe(
                take(1),
                tap(({ customerDetails }: { customerDetails: Customer }) => {
                    this.customerDetails = customerDetails;
                    this.handleFormData();
                }),
            )
            .subscribe();
    }

    editCustomer(customerPayload: CustomerPayload) {
        const { id } = this.customerDetails;
        this.customerService
            .updateCustomer(id, customerPayload)
            .pipe(
                take(1),
                tap(() => {
                    this.messageService.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: `تم تعديل ${this.pageName} بنجاح `,
                    });
                }),
                delay(200),
                tap(() => {
                    this.router.navigate([this.routeName]);
                }),
                catchError(() => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'حدث خطأ ما، الرجاء اعاده المحاوله',
                    });
                    return EMPTY;
                }),
            )
            .subscribe();
    }

    selectOnFocus(e: Event) {
        if ((e.target as HTMLInputElement).select) {
            (e.target as HTMLInputElement).select();
        }
    }

    onDateSelect($event: Date) {
        this.validDate = $event.toISOString().substring(0, 10);
    }

    save(): void {
        if (this.customForm.valid && this.customForm.dirty) {
            const formDate = {
                ...this.customForm.value,
                openingBalanceDate: this.validDate || this.customForm.value.openingBalanceDate,
            };
            this.btnSaved = true;
            this.editCustomer(formDate);
        }
    }
}
